// src/v2/leases/LeaseDrawerV3.jsx
//
// DRAWER-LEASE-V3: Unified lease peek drawer for both list and timeline views.
// Replaces the separate LeaseDrawer (list) and LeaseTimelineDrawer (timeline).
//
// Implements the v3 spec: photo band, ID/name/address header, three labeled
// chips, large RAG health pill with one-line reason, 2×3 KPI tiles, minimal
// 3-point term timeline, dates & options rows, related link cards, footer CTA.
//
// Props:
//   lease     object    — list row object (or null/undefined)
//   onClose   function  — called when the drawer should close
//   imageUrl  string?   — photo URL for the photo band (optional)
//   onPrev    function? — navigate to previous lease in the visible list
//   onNext    function? — navigate to next lease in the visible list
//   hasPrev   boolean?  — whether a previous lease exists
//   hasNext   boolean?  — whether a next lease exists

import React, { useEffect, useState } from "react";
import { currentLeaseFootprint } from "../../../shared/leaseAreaBasis.js";
import { listAnnualArea } from "../../shared/leaseAnnualHeadlines.js";
import { useNavigate } from "react-router-dom";
import { DrawerCloseButton } from "../components/DrawerCloseButton.jsx";
import { T, STAGES } from "../../app/tokens.js";
import { StatusPill } from "../components/StatusPill.jsx";
import { getLeaseById, listCriticalDates } from "../../data/leasesClient.js";
import { LEASE_DISPLAY } from "../../utils/leaseTypeConstants.js";
import {
  buildLeaseUrl,
  buildLocationUrl,
  buildMarketPlanUrl,
} from "../../services/buildUrl.js";
import { log } from "../../utils/logger.js";
import { statusFor } from "../../features/leases/pages/components/LeasePortfolioFilters.jsx";
import { LEASE_STATUS_META } from "../../features/leases/leaseStatusLabels.js";
import { STATUS_LABELS } from "../../features/leases/pages/components/LeasesListTable.jsx";
import {
  resolveTimelineEndAnchor,
  parseDateOnly,
} from "../../features/leases/leaseTabConfig.js";
import { computeLeaseRisk } from "../../features/leases/leaseRisk.js";
import { classifyLeaseHealth } from "../../features/leases/deriveLeaseActions.js";
import { BAND_CONTEXT } from "./LeasesTimelineView.jsx";
import { useFeatureFlagContext } from "../../contexts/FeatureFlagContext.jsx";
import { governedNavEnabled } from "../../app/governedNav.js";
import { formatLongExpiryDate, isActionablePastDueExpiry, isPastExpiryDate } from "../../features/leases/expiryDisplay.js";

// ── Health verdict config (maps action_window_bucket → display) ────────────

const HEALTH_CONFIG = {
  "decide-now":   { label: "Decide now",   color: T.ragRed,       bg: T.ragRedBg       },
  sublease:       { label: "Sublease",     color: T.ragAmber,     bg: T.ragAmberBg     },
  "winding-down": { label: "Winding-Down", color: "var(--v2-mid)", bg: "var(--v2-hair)" },
  negotiate:      { label: "Negotiate",    color: T.ragAmber,     bg: T.ragAmberBg     },
  model:          { label: "Model",        color: T.ragAmber,     bg: T.ragAmberBg     },
  monitor:        { label: "Secure",       color: T.ragGreenText, bg: T.ragGreenBg     },
  // LEASES-ACTIVE-OFFICE-EXPIRED-OUTLOOK-P1: In-Use lease past its anchor with
  // no future dates. Amber to signal data gap / action needed.
  holdover:       { label: "Holdover",     color: T.ragAmber,     bg: T.ragAmberBg     },
  // LEASES-OUTLOOK-DATA-FIXES: Draft leases have no term yet — neutral/slate.
  draft:          { label: "Draft",        color: T.light,        bg: "var(--v2-hair)" },
  // Pre-commencement: lease is signed but not yet active — neutral/hair.
  pre_commencement: { label: "Committed", color: "var(--v2-mid)", bg: "var(--v2-hair)" },
};

// ── Pure helpers ────────────────────────────────────────────────────────────

const _MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec",
];

function fmtDateLabel(dateStr) {
  if (!dateStr) return "—";
  const d = new Date(String(dateStr).slice(0, 10) + "T00:00:00");
  if (isNaN(d.getTime())) return "—";
  return `${_MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

function fmtRentUsd(val) {
  if (val == null) return "—";
  const n = Number(val);
  if (!isFinite(n)) return "—";
  if (n >= 1_000_000) { const s = (n / 1_000_000).toFixed(2); return `$${s.endsWith(".00") ? s.slice(0, -3) : s.endsWith("0") ? s.slice(0, -1) : s}M`; }
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtPsf(annualRentUsd, size) {
  if (annualRentUsd == null || !size || Number(size) <= 0) return "—";
  const psf = Number(annualRentUsd) / Number(size);
  if (!isFinite(psf)) return "—";
  return `$${psf.toFixed(2)}`;
}

function fmtSqft(size) {
  if (!size || Number(size) <= 0) return "—";
  return Number(size).toLocaleString("en-US", { maximumFractionDigits: 0 }) + " sf";
}

function termLeftMs(endDate) {
  if (!endDate) return null;
  const d = parseDateOnly(String(endDate).slice(0, 10));
  if (!d) return null;
  const ms = d.getTime() - Date.now();
  return ms > 0 ? ms : 0;
}

const MS_PER_MONTH = 30.4375 * 24 * 60 * 60 * 1000;

function fmtTermLeft(endDate) {
  const ms = termLeftMs(endDate);
  if (ms === null) return "—";
  if (ms === 0) return "Expired";
  const months = ms / MS_PER_MONTH;
  if (months < 12) return `${Math.round(months)} mo`;
  return `${(months / 12).toFixed(1)} yr`;
}

function fmtRemainingCommitment(annualRentUsd, endDate) {
  if (!annualRentUsd) return "—";
  const ms = termLeftMs(endDate);
  if (ms === null || ms === 0) return "—";
  const years = ms / (MS_PER_MONTH * 12);
  const total = Number(annualRentUsd) * years;
  if (!isFinite(total)) return "—";
  return fmtRentUsd(total);
}

function fmtStageLabel(stage) {
  if (!stage) return "—";
  return stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, " ");
}

// ── Sub-components ──────────────────────────────────────────────────────────

function KpiTile({ label, value, testId }) {
  return (
    <div
      data-testid={testId}
      style={{
        background:   T.hoverBg,
        borderRadius: 7,
        padding:      "7px 9px",
        minWidth:     0,
      }}
    >
      <div
        style={{
          fontSize:     T.fsSm5,
          fontWeight:   T.fwBold,
          color:        "var(--v2-dark)",
          lineHeight:   1.1,
          whiteSpace:   "nowrap",
          overflow:     "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: T.fsXxxs5, color: T.light, marginTop: 3 }}>
        {label}
      </div>
    </div>
  );
}

function DateRow({ label, value, testId, valueColor, dateKey, hoveredKey, onHoverIn, onHoverOut }) {
  const isHighlighted = dateKey != null && hoveredKey === dateKey;
  return (
    <div
      data-testid={testId}
      onMouseEnter={dateKey != null ? () => onHoverIn?.(dateKey) : undefined}
      onMouseLeave={dateKey != null ? () => onHoverOut?.()       : undefined}
      style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        padding:        "6px 0",
        borderBottom:   "1px solid var(--v2-hair)",
        gap:            8,
      }}
    >
      <span
        style={{
          fontSize:   T.fsSm,
          color:      isHighlighted ? "var(--v2-dark)" : T.light,
          fontWeight: isHighlighted ? T.fwSemibold    : undefined,
          flexShrink: 0,
          transition: "color 0.1s, font-weight 0.1s",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize:     T.fsBase,
          color:        valueColor || "var(--v2-dark)",
          fontWeight:   T.fwSemibold,
          textAlign:    "right",
          minWidth:     0,
          overflow:     "hidden",
          textOverflow: "ellipsis",
          whiteSpace:   "nowrap",
        }}
      >
        {value || "—"}
      </span>
    </div>
  );
}

function RelatedCard({ href, onClick, title, subtitle, testId }) {
  const sharedStyle = {
    display:        "flex",
    flexDirection:  "column",
    gap:            2,
    padding:        "8px 10px",
    background:     "var(--v2-tint)",
    borderRadius:   8,
    border:         "1px solid var(--v2-hair)",
    textDecoration: "none",
    color:          "inherit",
    width:          "100%",
    boxSizing:      "border-box",
    textAlign:      "left",
    cursor:         "pointer",
  };
  const content = (
    <>
      <span style={{ fontSize: T.fsSm5, fontWeight: T.fwSemibold, color: "var(--v2-accent)" }}>
        {title} →
      </span>
      {subtitle && (
        <span
          style={{
            fontSize:     T.fsXs,
            color:        T.light,
            whiteSpace:   "nowrap",
            overflow:     "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {subtitle}
        </span>
      )}
    </>
  );
  if (onClick) {
    return (
      <button type="button" data-testid={testId} onClick={onClick} style={sharedStyle}>
        {content}
      </button>
    );
  }
  return (
    <a href={href} data-testid={testId} style={sharedStyle}>
      {content}
    </a>
  );
}

// ── Event dot colors for the runway track (mirrors DatesLeaseTimeline) ───────

const RUNWAY_DOT_COLOR = {
  commencement:       T.blue,
  expiry_anchor:      T.ragRed,
  renewal_notice:     T.spacebaseBlue,
  break_option:       T.breakRed,
  break_notice:       T.purple,
  expiration:         T.expirationAmber,
  escalation:         T.provenancePdf,
  deposit_burndown:   T.forestGreen,
  allowance_deadline: T.spacebaseBlue,
  allowance_warning:  T.mid,
  allowance_expiry:   T.mid,
  cam_audit:          T.spacebaseBlue,
  other:              T.mid,
};

// Color for each activeDates key on the runway track.
const ACTIVE_DATE_DOT_COLOR = {
  break:          RUNWAY_DOT_COLOR.break_option,
  renewal_option: T.ragAmber,
};

// ── Term Runway bar ──────────────────────────────────────────────────────────

// activeDates      — array of { key, label, date } for intermediate dots only
//                    (Commenced/Expiry are terminal labels, not dot entries).
// hoveredDateKey   — key currently hovered (string | null); synced with date rows.
// onHoverKey       — (key | null) → void; called on dot mouseenter/leave.
// isPreCommencement — when true the lease hasn't started yet; show forward-looking
//                    track with "Commences [date]" label instead of "Commenced [date]"
//                    and suppress the elapsed fill / today marker.
function TermRunway({ commencedDate, expiryDate, expiryLabel, expiryColor, activeDates, hoveredDateKey, onHoverKey, isPreCommencement }) {
  const today     = new Date();
  const commenced = commencedDate
    ? parseDateOnly(String(commencedDate).slice(0, 10))
    : null;
  const expiry    = expiryDate
    ? parseDateOnly(String(expiryDate).slice(0, 10))
    : null;

  // For pre-commencement leases today is before the start date, so elapsed
  // fill / today marker are not meaningful.  We still compute span/yrsLeft so
  // the header can advertise the full term length.
  const isActuallyPreComm = isPreCommencement && commenced && commenced > today;

  let elapsedPct      = null;
  let yrsLeft         = null;
  let contractualYrs  = null; // expiry − commenced (for pre-commencement header)
  let span            = null;
  if (commenced && expiry && expiry > commenced) {
    span           = expiry.getTime() - commenced.getTime();
    contractualYrs = Math.round((span / (365.25 * 86400000)) * 10) / 10;
    if (!isActuallyPreComm) {
      const elapsed = today.getTime() - commenced.getTime();
      elapsedPct = Math.max(0, Math.min(100, (elapsed / span) * 100));
      const msLeft = expiry.getTime() - today.getTime();
      yrsLeft = msLeft > 0 ? Math.round((msLeft / (365.25 * 86400000)) * 10) / 10 : 0;
    }
  }

  // Time until commencement (for pre-commencement header copy).
  let timeToStart = null;
  if (isActuallyPreComm) {
    const msToStart = commenced.getTime() - today.getTime();
    const moToStart = msToStart / MS_PER_MONTH;
    timeToStart = moToStart < 12
      ? `${Math.round(moToStart)} mo`
      : `${(moToStart / 12).toFixed(1)} yr`;
  }

  // Map activeDates to positioned dot descriptors.  1-for-1 with the optional
  // date rows shown in "Dates & options" — no orphan dots possible.
  const eventDots = (() => {
    if (!span || !commenced) return [];
    return (activeDates || [])
      .map((entry) => {
        const d = parseDateOnly(String(entry.date).slice(0, 10));
        if (!d) return null;
        const pos = (d.getTime() - commenced.getTime()) / span;
        if (pos < 0 || pos > 1) return null;
        return { ...entry, pct: pos * 100 };
      })
      .filter(Boolean);
  })();

  const headerParts = ["Term runway"];
  if (isActuallyPreComm && timeToStart !== null) {
    headerParts.push(`commences in ${timeToStart}`);
    // Advertise the contractual term length (expiry − commenced), not expiry − today.
    if (contractualYrs !== null) headerParts.push(`${contractualYrs} yr${contractualYrs !== 1 ? "s" : ""} term`);
  } else {
    if (yrsLeft !== null) headerParts.push(`${yrsLeft} yr${yrsLeft !== 1 ? "s" : ""} left`);
    if (elapsedPct !== null) headerParts.push(`${Math.round(elapsedPct)}% elapsed`);
  }

  return (
    <div data-testid="v3-term-runway" style={{ marginBottom: 18 }}>
      {/* Header row */}
      <div style={{ fontSize: T.fsXxs5, color: T.light, marginBottom: 6 }}>
        {headerParts.join(" · ")}
      </div>

      {/* Small green Today caption immediately above the cap; clamp near the end. */}
      {elapsedPct !== null && (
        <div
          data-testid="v3-term-runway-today-caption-row"
          style={{ position: "relative", height: 20 }}
        >
          <span
            data-testid="v3-term-runway-today-caption"
            style={{
              position:      "absolute",
              left:          `${elapsedPct}%`,
              top:           0,
              transform:     elapsedPct >= 80
                ? "translateX(-100%)"
                : (elapsedPct <= 12 ? undefined : "translateX(-50%)"),
              fontSize:      T.fsXxxs5,
              lineHeight:    "12px",
              fontWeight:    T.fwSemibold,
              letterSpacing: ".03em",
              color:         T.ragGreenText,
              whiteSpace:    "nowrap",
            }}
          >
            Today
          </span>
        </div>
      )}

      {/* Track */}
      <div
        data-testid="v3-term-runway-track"
        style={{
          position:     "relative",
          height:       6,
          background:   "var(--v2-hair)",
          borderRadius: 3,
          margin:       "0 0 6px",
        }}
      >
        {/* Blue elapsed fill — only for active (non-pre-commencement) leases */}
        {elapsedPct !== null && (
          <div
            style={{
              position:     "absolute",
              left:         0,
              top:          0,
              width:        `${elapsedPct}%`,
              height:       "100%",
              background:   T.blue,
              borderRadius: 3,
            }}
          />
        )}
        {/* 3px × 10px Today cap straddles the bar's upper edge, never extending below it. */}
        {elapsedPct !== null && (
          <div
            data-testid="v3-term-runway-today"
            style={{
              position:     "absolute",
              left:         `${elapsedPct}%`,
              top:          -4,
              width:        3,
              height:       10,
              background:   T.blue,
              borderRadius: 2,
              transform:    elapsedPct >= 100
                ? "translateX(-100%)"
                : (elapsedPct <= 0 ? undefined : "translateX(-50%)"),
            }}
          />
        )}
        {/* Active-date event dots — 1-for-1 with optional "Dates & options" rows */}
        {eventDots.map((e) => {
          const isHovered = hoveredDateKey === e.key;
          const color = ACTIVE_DATE_DOT_COLOR[e.key] || T.mid;
          const dotSize = e.key === "renewal_option" ? 6 : 8;
          return (
            <div
              key={e.key}
              data-testid={`v3-term-runway-event-${e.key}`}
              title={`${e.label}: ${fmtDateLabel(e.date)}`}
              onMouseEnter={() => onHoverKey?.(e.key)}
              onMouseLeave={() => onHoverKey?.(null)}
              style={{
                position:     "absolute",
                left:         `${e.pct}%`,
                top:          "50%",
                width:        dotSize,
                height:       dotSize,
                borderRadius: "50%",
                background:   color,
                transform:    `translate(-50%, -50%) scale(${isHovered ? 1.5 : 1})`,
                filter:       isHovered ? "brightness(1.25)" : undefined,
                transition:   "transform 0.12s ease, filter 0.12s ease",
                cursor:       "pointer",
                zIndex:       isHovered ? 2 : 1,
              }}
            />
          );
        })}
      </div>

      {/* Renewal marker labels occupy their own row between the track and endpoint dates. */}
      {eventDots.some((event) => event.key === "renewal_option") && (
        <div
          data-testid="v3-term-runway-marker-label-row"
          style={{ position: "relative", height: 14 }}
        >
          {eventDots
            .filter((event) => event.key === "renewal_option")
            .map((event) => (
              <span
                key={event.key}
                data-testid="v3-term-runway-renewal-label"
                style={{
                  position:     "absolute",
                  left:         `${event.pct}%`,
                  top:          0,
                  transform:    "translateX(-50%)",
                  fontSize:     T.fsXxs5,
                  lineHeight:   "12px",
                  fontWeight:   T.fwBold,
                  color:        T.ragAmberText,
                  whiteSpace:   "nowrap",
                  pointerEvents:"none",
                }}
              >
                Renewal
              </span>
            ))}
        </div>
      )}

      {/* Endpoint dates remain on a separate baseline below marker labels. */}
      <div
        data-testid="v3-term-runway-endpoint-row"
        style={{
          display:        "flex",
          justifyContent: "space-between",
          alignItems:     "flex-start",
          marginTop:      3,
        }}
      >
        <span
          data-testid="v3-term-runway-commenced"
          style={{ fontSize: T.fsXxs, color: T.light }}
        >
          {isActuallyPreComm ? "Commences" : "Commenced"} {fmtDateLabel(commencedDate)}
        </span>
        <span
          data-testid="v3-term-runway-expiry"
          style={{ fontSize: T.fsXxs, color: expiryColor, textAlign: "right" }}
        >
          Expiry {expiryLabel}
        </span>
      </div>
    </div>
  );
}

// ── Main component ──────────────────────────────────────────────────────────

export function LeaseDrawerV3({ lease, onClose, imageUrl, onPrev, onNext, hasPrev, hasNext, orderedIds }) {
  const navigate = useNavigate();
  const { flags, flagsReady, nav_config, nav_order } = useFeatureFlagContext();
  const marketsEnabled = flagsReady && governedNavEnabled(flags, nav_config, "plan", nav_order);
  const officesEnabled = flagsReady && governedNavEnabled(flags, nav_config, "portfolio", nav_order);
  const [detail, setDetail] = useState(null);
  const [criticalDates, setCriticalDates] = useState(null);
  // Key of the date row / runway dot currently under the cursor (null = none).
  const [hoveredDateKey, setHoveredDateKey] = useState(null);

  useEffect(() => {
    if (!lease?.id || !lease?.locationId) return;
    let cancelled = false;
    Promise.allSettled([
      getLeaseById(lease.id),
      listCriticalDates(lease.locationId, lease.id),
    ])
      .then(([detailResult, cdsResult]) => {
        if (cancelled) return;
        if (detailResult.status === "fulfilled") {
          setDetail(detailResult.value);
        } else {
          log.warn("LeaseDrawerV3: detail fetch failed", {
            id: lease?.id,
            err: String(detailResult.reason),
          });
          setDetail({});
        }
        if (cdsResult.status === "fulfilled") {
          setCriticalDates(cdsResult.value || []);
        } else {
          log.warn("LeaseDrawerV3: criticalDates fetch failed", {
            id: lease?.id,
            err: String(cdsResult.reason),
          });
          setCriticalDates([]);
        }
      });
    return () => { cancelled = true; };
  }, [lease?.id, lease?.locationId]);

  if (!lease) return null;

  // ── Derived from list row ─────────────────────────────────────────────────

  // LEASE-DRAWER-OUTLOOK-UNKNOWN-SF: Derive the bucket fresh using the same
  // classifyLeaseHealth path as the list and timeline, so the drawer always
  // agrees with those surfaces instead of reading the possibly-null/stale
  // action_window_bucket field from the prop object.
  const { bucket } = classifyLeaseHealth(lease, criticalDates ?? []);

  // LEASES-OUTLOOK-DATA-FIXES: statusBucket drives the Draft override — a draft
  // lease with action_window_bucket="monitor" must NOT show "Secure".
  const statusBucket = statusFor(lease);
  const statusLabel  = STATUS_LABELS[statusBucket] || statusBucket || "—";

  const isDraft           = statusBucket === "draft";
  const isPreCommencement = statusBucket === "pre_commencement";
  const healthCfg = isDraft
    ? (HEALTH_CONFIG["draft"]            ?? { label: "Draft",            color: T.light, bg: "var(--v2-hair)" })
    : isPreCommencement
    ? (HEALTH_CONFIG["pre_commencement"] ?? { label: "Committed", color: T.light, bg: "var(--v2-hair)" })
    : (HEALTH_CONFIG[bucket]             ?? { label: "Unknown",          color: T.light, bg: "var(--v2-hair)" });
  const isSecure  = !isDraft && !isPreCommencement && bucket === "monitor";

  // Map leaseType → type so computeLeaseRisk (which reads lease.type) works
  // correctly when the prop comes from a list row (camelCase fields) rather
  // than a raw detail API response that already has a snake_case `type`.
  const risk = computeLeaseRisk({ ...lease, type: lease.type || lease.leaseType });

  // LEASES-OUTLOOK-DATA-FIXES: Use BAND_CONTEXT as the primary descriptor —
  // it explains the action-bucket ("2+ years out - watch and revisit later"),
  // not data-completeness gaps. topDriver (which can produce placeholder copy
  // like "Lease type not recorded.") is only used as a last-resort fallback
  // when no BAND_CONTEXT entry exists for the bucket.
  // Draft leases get a fixed copy since they have no action bucket yet.
  // Pre-commencement leases show the start date once detail has loaded.
  const _preCommStartDate = detail?.startDate || lease.startDate || null;
  const descriptor = isDraft
    ? "Lease is in draft — no term has been confirmed."
    : isPreCommencement
    ? (_preCommStartDate
        ? `Lease is signed but hasn't commenced yet — start date is ${fmtDateLabel(_preCommStartDate)}`
        : "Lease is signed but hasn't commenced yet.")
    : (BAND_CONTEXT[bucket] ?? risk?.topDriver ?? null);

  const anchor  = resolveTimelineEndAnchor(lease);
  const endDate = anchor.date || lease.endDate || null;

  const leaseTypeKey   = lease.leaseType || lease.type || "";
  const leaseTypeLabel = leaseTypeKey ? (LEASE_DISPLAY[leaseTypeKey] || leaseTypeKey) : "—";

  const locationStageKey = lease.locationStage || detail?.locationStage || "";
  const pastDueActionable = isActionablePastDueExpiry(lease, {
    date: endDate,
    officeStage: locationStageKey,
  });
  const expiryDisplayLabel = isPastExpiryDate(endDate) ? formatLongExpiryDate(endDate) : fmtDateLabel(endDate);
  const stageLabel = fmtStageLabel(locationStageKey);
  const stageInfo  = STAGES[locationStageKey] ?? null;

  const isHub      = !!(lease.isHub || lease.is_hub);
  const marketName = lease.city || "";
  const alias      = lease.locationAlias || "";

  // KPI Row 1
  // L-48c: for active/holdover leases with no rent data, show an amber "Rent not
  // sourced" badge next to the dash so editors know to enter data rather than assume
  // it is inapplicable.
  const _rentStatus = lease.status || "";
  const _rentMissing = lease.annualRentUsd == null &&
    (_rentStatus === "active" || _rentStatus === "holdover");
  const rentLabel = _rentMissing
    ? (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 3, flexWrap: "nowrap" }}>
        <span>—</span>
        <span
          data-testid="v3-rent-missing-badge"
          style={{
            fontSize:     T.fsXxs,
            fontWeight:   T.fwSemibold,
            padding:      "1px 5px",
            borderRadius: 4,
            background:   T.ragAmberBg,
            color:        T.ragAmberText,
            whiteSpace:   "nowrap",
          }}
        >
          Rent not sourced
        </span>
      </span>
    )
    : fmtRentUsd(lease.annualRentUsd);
  // $/sf: show "n/a" for serviced offices with no tracked area — the concept
  // doesn't apply (mirrors ServicedNaLabel pattern used in CostTab / LocationDrawerV2).
  // Show "—" when size is merely absent for a non-serviced lease (data not entered).
  const _leaseTypeStr      = lease.leaseType || lease.type || "";
  const currentSize = currentLeaseFootprint(lease).grossSf;
  const _isServicedNoArea = _leaseTypeStr === "serviced_office" && currentSize == null
    && currentLeaseFootprint(lease).source === "legacy_lease_size";
  const psfLabel = _isServicedNoArea ? "n/a" : fmtPsf(
    lease.listDisplayRentUsd !== undefined ? lease.listDisplayRentUsd : lease.annualRentUsd,
    listAnnualArea(lease));
  const sizeLabel = currentSize === 0 ? "0 sf" : fmtSqft(currentSize);

  // KPI Row 2
  const termLeft     = fmtTermLeft(endDate);
  const remaining    = fmtRemainingCommitment(lease.contractualAnnualRentUsd !== undefined ? lease.contractualAnnualRentUsd : lease.annualRentUsd, endDate);
  const nextDecision = lease.nextNoticeDeadline
    ? fmtDateLabel(lease.nextNoticeDeadline)
    : "—";

  // ── Detail-fetched fields ─────────────────────────────────────────────────

  const detailFetched       = detail !== null;
  const commencedDate       = detail?.startDate       ?? null;
  const breakDate           = detail?.breakDate       ?? null;
  const renewalCount        = detail?.renewalCount        != null ? Number(detail.renewalCount)        : 0;
  const renewalTermYrs      = detail?.renewalTermYears    != null ? Number(detail.renewalTermYears)    : 0;
  const renewalNoticeMonths = detail?.renewalNoticeMonths != null ? Number(detail.renewalNoticeMonths) : null;
  const lessorVendor        = detail?.vendor               ?? null;
  const noticeAddress       = detail?.noticeAddress        ?? lease.address ?? null;

  const structuralLabel = renewalCount > 0 && renewalTermYrs > 0
    ? `${renewalCount}×${renewalTermYrs}yr`
    : null;

  const _today = new Date();
  _today.setHours(0, 0, 0, 0);
  const _earliestRenewalDate = (criticalDates || [])
    .filter((cd) => cd.type === "renewal_notice" && cd.dueDate)
    .map((cd) => ({ cd, d: new Date(String(cd.dueDate).slice(0, 10) + "T00:00:00") }))
    .filter(({ d }) => !isNaN(d.getTime()) && d >= _today)
    .sort((a, b) => a.d - b.d)[0]?.cd?.dueDate ?? null;
  const renewalDateLabel = _earliestRenewalDate ? fmtDateLabel(_earliestRenewalDate) : null;

  const renewalOptionLabel = structuralLabel && renewalDateLabel
    ? `${structuralLabel} · ${renewalDateLabel}`
    : (structuralLabel || renewalDateLabel || "—");

  const renewalNoticeLabel = renewalNoticeMonths != null && renewalNoticeMonths > 0
    ? `${renewalNoticeMonths} mo before expiry`
    : "—";

  // Expiry row color matches health band
  const expiryColor = pastDueActionable
    ? T.ragRed
    : (isPastExpiryDate(endDate)
      ? T.mid
      : (isSecure ? T.ragGreenText : (bucket === "decide-now" ? T.ragRed : T.ragAmber)));
  const runwayExpiryColor = isPastExpiryDate(endDate)
    ? (pastDueActionable ? T.ragRed : T.mid)
    : (isSecure ? T.ragGreenText : T.ragRed);

  // ── market_id for deep-link expansion ────────────────────────────────────

  const marketId = detail?.marketId ?? null;

  // ── Active intermediate date entries (Commenced/Expiry are terminal labels) ─
  // A date entry is active when its underlying date resolves to a real value.
  // These are passed to TermRunway so dots are exactly 1-for-1 with date rows.
  const activeDates = [];
  if (breakDate && fmtDateLabel(breakDate) !== "—") {
    activeDates.push({ key: "break", label: "Break date", date: breakDate });
  }
  if (_earliestRenewalDate && fmtDateLabel(_earliestRenewalDate) !== "—") {
    activeDates.push({ key: "renewal_option", label: "Renewal option", date: _earliestRenewalDate });
  }

  // ── URL helpers ───────────────────────────────────────────────────────────

  const marketPlanUrl = buildMarketPlanUrl("plan",   marketId);
  const whatIfUrl     = buildMarketPlanUrl("whatif", marketId);
  const officeUrl     = buildLocationUrl(lease.locationId);
  const openLeaseUrl  = buildLeaseUrl(lease.id);

  return (
    <div
      data-testid="lease-drawer-v3"
      style={{
        height:        "100%",
        display:       "flex",
        flexDirection: "column",
        boxSizing:     "border-box",
      }}
    >
      {/* ── Photo band ───────────────────────────────────────────────────── */}
      <div
        data-testid="v3-drawer-photo-band"
        style={{
          position:   "relative",
          height:     148,
          flexShrink: 0,
          background: "transparent",
          overflow:   "hidden",
        }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt=""
            data-testid="v3-drawer-photo-img"
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
        <DrawerCloseButton onClick={onClose} testId="lease-drawer-close" />
      </div>

      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div style={{ padding: "12px 18px 0", flexShrink: 0 }}>
        <div
          style={{
            fontSize:      T.fsXxs5,
            color:         T.light,
            fontWeight:    T.fwSemibold,
            marginBottom:  3,
            letterSpacing: "0.04em",
          }}
        >
          {lease.id}
        </div>
        <div
          style={{
            display:    "flex",
            alignItems: "baseline",
            gap:        5,
            marginBottom: 4,
            flexWrap:   "nowrap",
            overflow:   "hidden",
          }}
        >
          {marketName && (
            <span
              style={{
                fontSize:   T.fsLg5,
                fontWeight: T.fwBold,
                color:      "var(--v2-dark)",
                whiteSpace: "nowrap",
              }}
            >
              {marketName}
            </span>
          )}
          {alias && (
            <>
              <span style={{ color: T.borderDark, flexShrink: 0 }}>·</span>
              <span
                style={{
                  fontSize:     T.fsBase,
                  color:        T.light,
                  whiteSpace:   "nowrap",
                  overflow:     "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {alias}
              </span>
            </>
          )}
          {isHub && (
            <span
              data-testid="v3-hub-badge"
              style={{
                fontSize:     T.fsXxs5,
                fontWeight:   T.fwBold,
                color:        T.hubChipText,
                background:   "rgba(90,79,207,.1)",
                borderRadius: 4,
                padding:      "1px 5px",
                whiteSpace:   "nowrap",
                flexShrink:   0,
              }}
            >
              ◆ Hub
            </span>
          )}
        </div>
        {noticeAddress && (
          <div
            data-testid="v3-drawer-address"
            style={{
              fontSize:     T.fsXs,
              color:        T.mid,
              marginBottom: 12,
              whiteSpace:   "nowrap",
              overflow:     "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {noticeAddress}
          </div>
        )}
      </div>

      {/* ── Chip row: Lease status · Office stage · Lease type ───────────── */}
      <div
        data-testid="v3-chip-row"
        style={{
          padding:      "4px 18px 14px",
          display:      "flex",
          gap:          20,
          flexWrap:     "wrap",
          flexShrink:   0,
          borderBottom: "1px solid var(--v2-hair)",
        }}
      >
        <StatusPill
          label="Lease status:"
          value={statusLabel}
          testId="v3-chip-status"
          bg={LEASE_STATUS_META[statusBucket]?.bg}
          fg={LEASE_STATUS_META[statusBucket]?.color}
        />
        <StatusPill
          label="Office stage:"
          value={stageLabel}
          testId="v3-chip-stage"
          bg={stageInfo?.bg}
          fg={stageInfo?.color}
        />
        <StatusPill label="Lease type:" value={leaseTypeLabel} testId="v3-chip-type" />
      </div>

      {/* ── Lease Outlook (swatch + text, matching LocationDrawerV2 Space Fit) */}
      <div style={{ padding: "10px 18px 16px", flexShrink: 0 }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          {/* 44×44 colored square swatch */}
          <span
            data-testid="v3-health-swatch"
            style={{
              display:      "inline-block",
              width:        44,
              height:       44,
              borderRadius: 11,
              flexShrink:   0,
              background:   healthCfg.bg,
            }}
          />
          <div>
            <div
              data-testid="v3-health-label"
              style={{
                fontSize:   T.fsXs5,
                fontWeight: T.fwBold,
                color:      healthCfg.color,
                lineHeight: 1.2,
              }}
            >
              {healthCfg.label}
            </div>
            <div style={{ fontSize: T.fsXxxs5, color: T.light, lineHeight: 1.4 }}>
              Lease outlook
              {risk?.partial && !isDraft && (
                <span
                  data-testid="v3-health-partial-badge"
                  style={{
                    marginLeft:  5,
                    fontSize:    T.fsXxs,
                    color:       T.ragAmber,
                    fontWeight:  T.fwSemibold,
                  }}
                >
                  · Incomplete data
                </span>
              )}
            </div>
            {descriptor && (
              <div
                data-testid="v3-health-reason"
                style={{ fontSize: T.fsXxs5, color: T.mid, lineHeight: 1.4, marginTop: 1 }}
              >
                {descriptor}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── KPI tiles — 2 rows × 3 ───────────────────────────────────────── */}
      <div style={{ padding: "0 18px 16px", flexShrink: 0 }}>
        <div
          data-testid="v3-kpi-row1"
          style={{
            display:             "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap:                 6,
            marginBottom:        6,
          }}
        >
          <KpiTile label="Size"      value={sizeLabel}  testId="v3-kpi-size" />
          <KpiTile label="Rent / yr" value={rentLabel}  testId="v3-kpi-rent" />
          <KpiTile label="$ / sf"    value={psfLabel}   testId="v3-kpi-psf"  />
        </div>
        <div
          data-testid="v3-kpi-row2"
          style={{
            display:             "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap:                 6,
          }}
        >
          <KpiTile label="Term left"            value={termLeft}   testId="v3-kpi-term"          />
          <KpiTile label="Remaining commitment" value={remaining}  testId="v3-kpi-remaining"      />
          <KpiTile label="Next decision"        value={nextDecision} testId="v3-kpi-next-decision" />
        </div>
      </div>

      {/* ── Term runway bar ──────────────────────────────────────────────── */}
      <div style={{ padding: "0 18px", flexShrink: 0 }}>
        <TermRunway
          commencedDate={commencedDate}
          expiryDate={endDate}
          expiryLabel={expiryDisplayLabel}
          expiryColor={runwayExpiryColor}
          activeDates={activeDates}
          hoveredDateKey={hoveredDateKey}
          onHoverKey={setHoveredDateKey}
          isPreCommencement={isPreCommencement}
        />
      </div>

      {/* ── Dates & options ──────────────────────────────────────────────── */}
      <div style={{ padding: "0 18px 16px", flexShrink: 0 }}>
        <div
          style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            textTransform: "uppercase",
            letterSpacing: ".07em",
            color:         T.light,
            marginBottom:  4,
          }}
        >
          Dates &amp; Options
        </div>
        <DateRow
          label={isPreCommencement ? "Commences" : "Commenced"}
          value={detailFetched ? fmtDateLabel(commencedDate) : "…"}
          testId="v3-date-commenced"
        />
        <DateRow
          label="Renewal notice"
          value={detailFetched ? renewalNoticeLabel : "…"}
          testId="v3-date-renewal-notice"
        />
        <DateRow
          label="Break date"
          value={detailFetched ? fmtDateLabel(breakDate) : "…"}
          testId="v3-date-break"
          dateKey="break"
          hoveredKey={hoveredDateKey}
          onHoverIn={setHoveredDateKey}
          onHoverOut={() => setHoveredDateKey(null)}
        />
        <DateRow
          label="Renewal option"
          value={detailFetched ? renewalOptionLabel : "…"}
          testId="v3-date-renewal-option"
          dateKey="renewal_option"
          hoveredKey={hoveredDateKey}
          onHoverIn={setHoveredDateKey}
          onHoverOut={() => setHoveredDateKey(null)}
        />
        <DateRow
          label="Expiry"
          value={expiryDisplayLabel}
          valueColor={expiryColor}
          testId="v3-date-expiry"
        />
        <DateRow
          label="Lessor"
          value={detailFetched ? (lessorVendor || "—") : "…"}
          testId="v3-date-lessor"
        />
      </div>

      {/* ── Related ──────────────────────────────────────────────────────── */}
      {(marketsEnabled || officesEnabled) && <div style={{ padding: "0 18px 16px", flexShrink: 0 }}>
        <div
          style={{
            fontSize:      T.fsXxs,
            fontWeight:    T.fwBold,
            textTransform: "uppercase",
            letterSpacing: ".07em",
            color:         T.light,
            marginBottom:  8,
          }}
        >
          Related
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {marketsEnabled && <RelatedCard
            onClick={() => navigate(marketPlanUrl)}
            title="Market Plan"
            subtitle={marketName || undefined}
            testId="v3-related-market-plan"
          />}
          {marketsEnabled && <RelatedCard
            onClick={() => navigate(whatIfUrl)}
            title="Scenario"
            subtitle="Model a scenario"
            testId="v3-related-whatif"
          />}
          {officesEnabled && <RelatedCard
            onClick={() =>
              navigate(officeUrl, {
                state: {
                  preloadLoc: {
                    id:            lease.locationId,
                    locationAlias: alias,
                    market:        marketName,
                  },
                },
              })
            }
            title="Office"
            subtitle={alias || marketName || undefined}
            testId="v3-related-office"
          />}
        </div>
      </div>}

      {/* ── Spacer ───────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 16 }} />

      {/* ── Footer: Open lease → ─────────────────────────────────────────── */}
      <div style={{ padding: "16px 18px 24px", flexShrink: 0 }}>
        <button
          type="button"
          data-testid="v3-open-lease-link"
          onClick={() => navigate(openLeaseUrl, {
            state: Array.isArray(orderedIds) && orderedIds.length > 0
              ? { orderedIds }
              : undefined,
          })}
          style={{
            display:        "block",
            width:          "100%",
            padding:        "11px 0",
            textAlign:      "center",
            background:     "var(--v2-accent)",
            color:          T.white,
            borderRadius:   10,
            fontSize:       T.fsBase,
            fontWeight:     T.fwBold,
            border:         "none",
            cursor:         "pointer",
            letterSpacing:  "0.01em",
            boxSizing:      "border-box",
          }}
        >
          Open lease →
        </button>
      </div>
    </div>
  );
}
